/**
 *
 * UsersDetail
 *
 */

import React, { memo, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import './style.scss';

import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';
import reducer from './reducer';
import saga from './saga';

import { flushState, loadUser } from './actions';
import {
  makeSelectLoading,
  makeSelectError,
  makeSelectUser,
} from './selectors';

// antd component
import { Col, Row, Tabs, Form, Select } from 'antd';
const { TabPane } = Tabs;

// assets
//import LogoClient from 'images/logo.svg';
//import CustomIcons from 'components/CustomIcons';

// components
import Preloader from 'components/Preloader';
import CustomPageHeader from 'components/CustomPageHeader';
import CustomForm from 'components/Forms/CustomForm';

// init forms
import { initFormsByRole } from 'utils/forms/forms.init';
// Custom Tabs
import TabStudents from 'components/Tabs/TabStudents';
import TabCourses from 'components/Tabs/TabCourses';
import RenderPopconfirm from 'components/RenderPopconfirm';

// helper for forms
import { isEqualFormValues } from 'utils/forms/forms.utils';


export function UsersDetail({
  flushState,
  // reducer main props
  loading,
  userData,
  loadUser,
  // custom props
  currentId,
  update,
  updatedItem,
  // form actions and data
  form,
  generalForm,
  setGeneralForm,
  // parent states
  formHaveChanges,
  setFormHaveChanges,
  currentElement,
  setCurrentElement,
}) {
  useInjectReducer({ key: 'usersDetail', reducer });
  useInjectSaga({ key: 'usersDetail', saga });

  const newKey = 'nouvel';

  const [initID, setInitID] = useState(currentId);

  // states for tabs toggle
  const [activeTab, setActiveTab] = useState('general');
  const [stayActiveTab, setStayActiveTab] = useState(false);
  const [formMethod, setFormMethod] = useState('update');

  // states for main data
  const [currentStudents, setCurrentStudents] = useState([]);
  const [currentCourses, setCurrentCourses] = useState([]);

  // form for parent
  const initRole = 'parent';
  const initForm = initFormsByRole(initRole);

  // Form initialization for each tab
  const [roleForm] = Form.useForm();
  const { customForm, resetForm, submitForm, setFormData } = form;

  /**
   * Loading data for rendering in a component
   * - work like ComponentDidUpdate
   */
  useEffect(() => {
    // reset data in the general form
    resetForm();
    setActiveTab('general');
    setFormMethod('update');
    // refresh state for new item
    if (!currentId || currentId == newKey) {
      // Set default data for NEW Item
      setCurrentElement(false);
      roleForm.setFieldsValue({ userRole: initRole });
      setGeneralForm(initForm);
      setFormMethod(newKey);
    } else if (currentId && currentId !== newKey) {
      setCurrentElement(false);
      loadUser(currentId);
    } else setInitID(currentId);
  }, [currentId]);

  // Set data for opened item
  useEffect(() => {
    if (
      (userData?._id && !userData?.code) ||
      (updatedItem?._id && !updatedItem?.code)
    ) {
      // check if updated data the same as current data
      let initData;
      if (currentId == updatedItem?._id || initID == newKey) {
        initData = updatedItem;
      } else {
        initData =
          userData?._id == updatedItem?._id
            ? updatedItem || userData
            : userData;
      }

      userData?._id != updatedItem?._id && setActiveTab('general');

      initData?._id && setInitID(initData?._id);
      setCurrentElement(initData);

      // set students list
      if (
        JSON.stringify(currentStudents) !== JSON.stringify(initData?.students)
      ) {
        setCurrentStudents(initData?.students);
      }

      // set courses list
      if (
        JSON.stringify(currentCourses) !== JSON.stringify(initData?.courses)
      ) {
        setCurrentCourses(initData?.courses);
      }

      // change general form by role
      const currnetForm = initFormsByRole(initData?.role);
      setGeneralForm(currnetForm);
      roleForm.setFieldsValue({ userRole: initData?.role });

      // reset data in the general form
      setFormData(initData, currnetForm);
    } else if (
      (userData && userData?.code != 200) ||
      (updatedItem && !updatedItem?.code != 200)
    ) {
      checkFromChanges(true);
    }
  }, [userData, updatedItem]);

  // Clearing the state after unmounting a component
  useEffect(() => {
    return () => {
      // Anything in here is fired on component unmount.
      flushState();
    };
  }, []);

  // form for selec of user role
  const userRoleForm = user => (
    <Form
      key="userRoleForm"
      form={roleForm}
      name="roleForm"
      onFieldsChange={changeUserRole}
      autoComplete="off"
    >
      <Form.Item name="userRole">
        <Select
          className="user-role-select"
          optionFilterProp="children"
          getPopupContainer={trigger => trigger.parentElement}
        >
          <Select.Option key="parent" value="parent" title="Parent">
            Parent
          </Select.Option>
          <Select.Option key="teacher" value="teacher" title="Enseignant">
            Enseignant
          </Select.Option>
          <Select.Option key="admin" value="admin" title="Admin">
            Admin
          </Select.Option>
        </Select>
      </Form.Item>
    </Form>
  );
  const setRole = role => {
    const tempUser = currentElement;
    setCurrentElement({ ...tempUser, role: role });
    // change general form by role
    const currnetForm = initFormsByRole(role);
    setGeneralForm(currnetForm);
    roleForm.setFieldsValue({ userRole: role });

    const formValues = customForm.getFieldsValue();
    checkFormValues(formValues);
  };

  const checkFormValues = useCallback(
    allValues => {
      const formVals = Object.values(allValues);
      const formHasAnyVals = formVals.some(
        val => val != '' && val != undefined && val != null,
      );
      const isEqualValuesWithElement = isEqualFormValues(
        generalForm,
        currentElement,
        allValues,
      );
      const hasCurrentElem =
        (currentElement && Object.keys(currentElement).length > 1) ||
        (currentElement &&
          Object.keys(currentElement).length == 1 &&
          !currentElement?.role);

      if (
        (!isEqualValuesWithElement && hasCurrentElem) ||
        (!isEqualValuesWithElement && formHasAnyVals && !hasCurrentElem)
      ) {
        checkFromChanges(true);
      } else {
        checkFromChanges(false);
      }
    },
    [currentElement, generalForm],
  );

  const changeUserRole = changedFields => {
    if (changedFields[0].name[0] == 'userRole') {
      setRole(changedFields[0].value);
      // set if form have any changes
      //setFormHaveChanges(true);
      // setStayActiveTab(true);
    }
  };

  // General Form action
  const onSubmitForm = data => {
    const updatedData = {
      ...currentElement,
      ...data,
    };
    update(updatedData);
    checkFromChanges(false);
   // message.success('Données enregistrées avec succès');
  };

  const onFormChange = useCallback((changedFields, allFields) => {
    checkFromChanges(true);
  }, []);

  const onFormValuesChange = useCallback(
    (changedValues, allValues) => {
      checkFormValues(allValues);
    },
    [currentElement, generalForm],
  );

  // Set if form have any changes
  const checkFromChanges = useCallback(haveChange => {
    setFormHaveChanges(haveChange);
    setStayActiveTab(haveChange);
  }, []);

  // Remove/Add (students, teachers, parents)
  const updateTableList = (array, type, actionType = 'update') => {
    if (array) {
      const updatedElem = { ...currentElement };
      if (type === 'students') {
        updatedElem.students = array;
      } else if (type === 'parents') {
        updatedElem.parents = array;
      } else if (type === 'courses') {
        updatedElem.courses = array;
      }
      update(updatedElem, actionType);
    }
  };

  /*
   * Сhange the way to switch tabs,
   * if there are changes in the form in the active tab
   */
  const onChangeTab = (key, event) => {
    if (formHaveChanges) {
      event.stopPropagation();
      !stayActiveTab && setActiveTab(key);
    } else {
      setActiveTab(key);
    }
  };

  /*
   * Custom tab title. Added Popconfirm window,
   * if there are changes in the form in the active tab
   */
  const renderTabTitle = useCallback(
    (tabTitle, key) => (
      <RenderPopconfirm
        showPopup={formHaveChanges}
        title={tabTitle}
        key={key}
        onCancel={() => {
          setActiveTab(key);
          setStayActiveTab(false);
          setFormHaveChanges(false);
          // update data in the general form
          setFormData();
        }}
        onConfirm={() => {
          setActiveTab(key);
          setStayActiveTab(false);
          setFormHaveChanges(false);
          submitForm();
        }}
      />
    ),
    [formHaveChanges, customForm, generalForm, currentElement],
  );

  /*
   * TABS Content
   */
  const tabVacation = () => {
    return (
      <TabPane
        disabled
        tab={renderTabTitle('Demande de congé', 'de_conge')}
        key="de_conge"
      >
        Demande de congé tab
      </TabPane>
    );
  };
  const tabInterviews = () => {
    return (
      <TabPane
        disabled
        tab={renderTabTitle('Entretiens', 'entretiens')}
        key="entretiens"
      >
        Entretiens tab
      </TabPane>
    );
  };
  const tabAdvanced = () => {
    return (
      <TabPane
        disabled
        tab={renderTabTitle('Gestion avançée', 'gestion_avancee')}
        key="gestion_avancee"
      >
        Gestion avançée tab
      </TabPane>
    );
  };

  // Rendering another tabs, based on user role
  const otherTabs = role => {
    switch (role) {
      case 'teacher':
        return (
          <TabPane tab={renderTabTitle('Cours', 'cours')} key="cours">
            <TabCourses
              courses={currentCourses}
              classId={currentElement?.class_info?._id}
              updateList={(array, actionType) => updateTableList(array, 'courses', actionType)}
              isTeacher={currentElement?._id}
              canAddNew
            />
          </TabPane>
        );
      case 'parent':
        return (
          <>
            {
              <TabPane
                tab={renderTabTitle('Étudiants', 'etudiants')}
                key="etudiants"
              >
                <TabStudents
                  showForParent={true}
                  students={currentStudents}
                  updateList={(array, actionType) => updateTableList(array, 'students', actionType)}
                />
              </TabPane>
            }
            {tabVacation()}
            {tabInterviews()}
            {tabAdvanced()}
          </>
        );
      case 'student':
        return '';
      case 'admin':
        return (
          <TabPane
            disabled
            tab={renderTabTitle('Admin', 'admin')}
            key="admin"
          >
            Info for Admin
          </TabPane>
        );
    }
  };


  // render function
  return (
    <div className="details-container">
      <Helmet>
        <title>Users</title>
        <meta name="description" content="Description of Users" />
      </Helmet>
      {!initID && loading && <Preloader />}
      {!loading && initID && (
        <>
          <CustomPageHeader
            title={
              currentElement?.description?.firstname && initID !== newKey
                ? `${currentElement?.description?.lastname} ${
                    currentElement?.description?.firstname
                  }`
                : 'Nouvel utilisateur'
            }
            subTitle={'Utilisateur' || currentElement?.role}
            subTitlePosition="top"
            extraActions={[userRoleForm(currentElement)]}
          />
          <Row gutter={[0, 0]}>
            <Col xs={24}>
              <Tabs
                defaultActiveKey={activeTab}
                activeKey={activeTab}
                className="details-tabs"
                onTabClick={onChangeTab}
              >
                <TabPane
                  tab={renderTabTitle('Informations générales', 'general')}
                  key="general"
                  forceRender
                >
                  <Row>
                    <Col span={24}>
                      <CustomForm
                        name={`users-general-form-${initID}`}
                        method={formMethod}
                        form={customForm}
                        dataSource={generalForm || initForm}
                        handleSubmit={data => onSubmitForm(data)}
                        // handleChange={onFormChange}
                        handleValuesChange={onFormValuesChange}
                        labelCol={24}
                        wrapperCol={24}
                        disabeled={!formHaveChanges}
                      />
                    </Col>
                  </Row>
                </TabPane>
                {currentId &&
                  currentId !== newKey &&
                  otherTabs(currentElement?.role || initRole)}
              </Tabs>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
}

UsersDetail.propTypes = {
  dispatch: PropTypes.func.isRequired,
  flushState: PropTypes.func.isRequired,
  update: PropTypes.func,
  loading: PropTypes.bool,
  currentId: PropTypes.oneOfType([
    PropTypes.bool.isRequired,
    PropTypes.number.isRequired,
    PropTypes.string.isRequired,
  ]),
};

const mapStateToProps = createStructuredSelector({
  loading: makeSelectLoading(),
  error: makeSelectError(),
  userData: makeSelectUser(),
});

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    flushState: () => dispatch(flushState()),
    loadUser: currnetId => dispatch(loadUser(currnetId)),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(UsersDetail);
